<template>
  <v-container fluid class="bus-dispatching-page pa-3">
    <!-- ══ Hero Header ══ -->
    <div class="dispatch-hero mb-3">
      <v-layout row align-center>
        <v-flex>
          <v-layout row align-center>
            <v-icon class="dispatch-hero-icon mr-3" size="36"
              >mdi-headset</v-icon
            >
            <div>
              <div class="dispatch-hero-title">Transportation Dispatching</div>
              <div class="dispatch-hero-sub">
                Live bus change lookup for dispatcher calls
              </div>
            </div>
          </v-layout>
        </v-flex>
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          outline
          dark
          class="dispatch-refresh-btn"
          :loading="loading"
          @click="loadDispatcherData"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-layout>
    </div>

    <!-- ══ Filter Panel ══ -->
    <v-card class="filter-card mb-3">
      <!-- Header bar -->
      <div class="filter-header">
        <v-icon class="filter-header-icon mr-2">mdi-tune</v-icon>
        <span class="filter-header-title">Dispatcher Lookup</span>
        <v-spacer></v-spacer>
        <v-btn flat small class="filter-clear-btn" @click="clearFilters">
          <v-icon left small>mdi-filter-remove</v-icon>
          Clear Filters
        </v-btn>
      </div>

      <div class="filter-body">
        <!-- Quick search — full width prominent -->
        <div class="filter-search-wrap mb-3">
          <v-text-field
            v-model="filters.generalSearch"
            prepend-inner-icon="mdi-magnify"
            label="Search anything — student, address, phone, note, bus…"
            clearable
            solo
            hide-details
            class="filter-main-search"
          ></v-text-field>
        </div>

        <!-- Narrow filters row -->
        <div class="filter-group-label">Narrow by</div>
        <v-layout row wrap class="filter-fields-row">
          <v-flex xs12 sm3 class="filter-field-wrap">
            <v-text-field
              v-model="filters.studentName"
              prepend-inner-icon="mdi-account"
              label="Student Name"
              clearable
              outline
              dense
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm2 class="filter-field-wrap">
            <v-autocomplete
              v-model="filters.busNumber"
              :items="availableBusNumbers"
              prepend-inner-icon="mdi-bus"
              label="Bus #"
              clearable
              outline
              dense
              hide-details
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm3 class="filter-field-wrap">
            <v-text-field
              v-model="filters.phoneNumber"
              prepend-inner-icon="mdi-phone"
              label="Phone Number"
              mask="###-###-####"
              clearable
              outline
              dense
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm2 class="filter-field-wrap">
            <v-autocomplete
              v-model="filters.branch"
              :items="availableBranches"
              prepend-inner-icon="mdi-source-branch"
              label="Branch"
              clearable
              outline
              dense
              hide-details
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm2 class="filter-field-wrap">
            <v-select
              v-model="filters.status"
              :items="availableStatuses"
              prepend-inner-icon="mdi-flag"
              label="Status"
              clearable
              outline
              dense
              hide-details
            ></v-select>
          </v-flex>
        </v-layout>

        <!-- Toggle row -->
        <div class="filter-toggle-row mt-3">
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--on': filters.activeOnCallDateOnly }"
            @click="filters.activeOnCallDateOnly = !filters.activeOnCallDateOnly"
          >
            <v-icon small class="mr-1">mdi-calendar-today</v-icon>
            Active Today Only
          </div>
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--completed': filters.includeCompleted }"
            @click="filters.includeCompleted = !filters.includeCompleted"
          >
            <v-icon small class="mr-1">mdi-check-circle</v-icon>
            Include Completed
          </div>
          <div
            class="filter-toggle-pill"
            :class="{ 'filter-toggle-pill--deleted': filters.includeDeleted }"
            @click="filters.includeDeleted = !filters.includeDeleted"
          >
            <v-icon small class="mr-1">mdi-delete</v-icon>
            Include Deleted
          </div>
        </div>
      </div>
    </v-card>

    <!-- ══ Results summary bar ══ -->
    <div class="results-bar mb-2">
      <v-layout row align-center>
        <v-flex>
          <span class="results-count">
            <strong>{{ filteredBusChanges.length }}</strong> result<span
              v-if="filteredBusChanges.length !== 1"
              >s</span
            >
            <span class="grey--text ml-1"
              >of {{ busChanges.length }} total</span
            >
          </span>
          <v-chip
            v-if="userBranch"
            small
            class="ml-2"
            color="blue lighten-5"
            text-color="blue darken-2"
          >
            <v-icon left small>mdi-source-branch</v-icon>
            {{ userBranch }}
          </v-chip>
        </v-flex>
        <v-spacer></v-spacer>
        <span class="dispatch-view-badge">
          <v-icon small class="mr-1">mdi-phone-in-talk</v-icon>
          Dispatch View
        </span>
      </v-layout>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="3"
        class="mt-1"
      ></v-progress-linear>
    </div>

    <!-- ══ Empty state ══ -->
    <v-alert
      v-if="
        !loading && busChanges.length > 0 && filteredBusChanges.length === 0
      "
      type="info"
      outline
      class="mt-2"
    >
      No bus changes matched the current lookup filters.
    </v-alert>

    <v-alert
      v-if="!loading && busChanges.length === 0"
      type="info"
      outline
      class="mt-2"
    >
      No bus changes loaded. Click Refresh to reload.
    </v-alert>

    <!-- ══ Results: expansion panels (Vuetify 1.x API) ══ -->
    <v-expansion-panel
      class="dispatch-panels"
      v-if="filteredBusChanges.length > 0"
    >
      <v-expansion-panel-content
        v-for="busChange in filteredBusChanges"
        :key="busChange.id"
      >
        <!-- Header slot -->
        <template v-slot:header>
          <div class="panel-header-inner">
            <!-- status bar on left -->
            <div
              class="panel-status-bar"
              :style="{ backgroundColor: getStatusHex(busChange.status) }"
            ></div>

            <!-- name + ids -->
            <div class="panel-col panel-name-col">
              <div class="panel-student-heb" v-if="busChange.studentNameHeb">
                {{ busChange.studentNameHeb }}
              </div>
              <div class="panel-student-name">
                {{ busChange.studentName || "Unknown Student" }}
              </div>
              <div class="panel-ids">
                Family&nbsp;
                <strong>{{ busChange.familyID || "—" }}</strong>
                &nbsp;·&nbsp; Student&nbsp;
                <strong>{{ busChange.studentID || "—" }}</strong>
              </div>
            </div>

            <!-- bus badge(s) -->
            <div class="panel-col panel-bus-col">
              <div class="panel-field-label">BUS #</div>
              <div
                v-if="busChange.hasDifferentBusNumbers"
                class="panel-bus-dual-row"
              >
                <div
                  class="panel-bus-badge panel-bus-badge--sm"
                  :style="{ borderColor: getStatusHex(busChange.status) }"
                >
                  <span class="panel-bus-dir">AM</span
                  >{{ busChange.pickupBusNumber }}
                </div>
                <div
                  class="panel-bus-badge panel-bus-badge--sm"
                  :style="{ borderColor: '#ff9800' }"
                >
                  <span class="panel-bus-dir">PM</span
                  >{{ busChange.dropoffBusNumber }}
                </div>
              </div>
              <div
                v-else
                class="panel-bus-badge"
                :style="{ borderColor: getStatusHex(busChange.status) }"
              >
                {{ busChange.primaryBusNumber || "—" }}
              </div>
            </div>

            <!-- dates -->
            <div class="panel-col panel-dates-col">
              <div class="panel-field-label">SCHEDULE</div>
              <div class="panel-field-value">
                {{ formatDate(busChange.startDate) }}
              </div>
              <div class="panel-field-value panel-dates-end">
                to {{ formatDate(busChange.endDate) }}
              </div>
            </div>

            <!-- period -->
            <div class="panel-col panel-period-col">
              <div class="panel-field-label">PERIOD</div>
              <div class="panel-field-value">
                {{ formatPeriodShort(busChange.startPeriod) }} →
                {{ formatPeriodShort(busChange.endPeriod) }}
              </div>
            </div>

            <!-- bus change contact -->
            <div class="panel-col panel-contact-col">
              <div class="panel-field-label">BUS CHANGE CONTACT</div>
              <div v-if="busChange.busChangeName" class="panel-contact-name">
                {{ busChange.busChangeName }}
              </div>
              <div v-if="busChange.busChangePhoneNumber" class="panel-contact-phone">
                <v-icon x-small class="mr-1" color="#2e7d32">mdi-phone</v-icon>{{ formatPhoneNumber(busChange.busChangePhoneNumber) }}
              </div>
            </div>

            <!-- class & teacher / DC room (season-based) -->
            <div class="panel-col panel-classtch-col">
              <div class="panel-field-label">
                {{
                  isBusChangeInSummerSeason(busChange)
                    ? "CLASS & ROOM"
                    : "CLASS & TEACHER"
                }}
              </div>
              <div class="panel-field-value">
                {{ busChange.branch || "—" }} / {{ busChange.class || "—" }}
              </div>
              <div
                v-if="
                  isBusChangeInSummerSeason(busChange) &&
                    getRoomDisplay(busChange)
                "
                class="panel-contact-teacher"
                :title="'Room ' + getRoomDisplay(busChange)"
              >
                <v-icon x-small class="mr-1" color="#78909c">mdi-school</v-icon>
                <span class="panel-teacher-text"
                  >Room {{ getRoomDisplay(busChange) }}</span
                >
              </div>
              <div
                v-else-if="
                  !isBusChangeInSummerSeason(busChange) &&
                    (busChange.teacherH || busChange.teacherH2)
                "
                class="panel-contact-teacher"
                :title="[busChange.teacherH, busChange.teacherH2].filter(Boolean).join(' / ')"
              >
                <v-icon x-small class="mr-1" color="#78909c">mdi-school</v-icon>
                <span class="panel-teacher-text">{{ busChange.teacherH }}<template v-if="busChange.teacherH && busChange.teacherH2"> / </template>{{ busChange.teacherH2 }}</span>
              </div>
            </div>

            <!-- status chip -->
            <div class="panel-col panel-status-col">
              <v-chip
                small
                :color="getStatusColor(busChange.status)"
                text-color="white"
                class="panel-status-chip"
              >
                <v-icon left small color="white">{{
                  getStatusIcon(busChange.status)
                }}</v-icon>
                {{ busChange.status || "UNKNOWN" }}
              </v-chip>
            </div>
          </div>
        </template>

        <!-- Expanded body -->
        <div class="panel-body">
          <v-layout row wrap>
            <!-- Left: bus change details -->
            <v-flex xs12 sm6 class="pr-2">
              <div class="detail-section">
                <div class="detail-section-title">
                  <v-icon small class="mr-1">mdi-bus-clock</v-icon>
                  Bus Change Details
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6>
                    <div class="detail-field">
                      <span class="detail-label">Bus Change Name</span>
                      <span class="detail-val">{{
                        busChange.busChangeName || "—"
                      }}</span>
                    </div>
                    <div class="detail-field">
                      <span class="detail-label">Address</span>
                      <span class="detail-val">{{
                        busChange.busChangeAddress || "—"
                      }}</span>
                    </div>
                    <div class="detail-field">
                      <span class="detail-label">Start Period</span>
                      <span class="detail-val">{{
                        formatPeriod(busChange.startPeriod)
                      }}</span>
                    </div>
                    <div class="detail-field">
                      <span class="detail-label">End Period</span>
                      <span class="detail-val">{{
                        formatPeriod(busChange.endPeriod)
                      }}</span>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <div class="detail-field">
                      <span class="detail-label">Start Date</span>
                      <span class="detail-val">{{
                        formatDate(busChange.startDate)
                      }}</span>
                    </div>
                    <div class="detail-field">
                      <span class="detail-label">End Date</span>
                      <span class="detail-val">{{
                        formatDate(busChange.endDate)
                      }}</span>
                    </div>
                  </v-flex>
                </v-layout>
                <div class="detail-field mt-1" v-if="busChange.note">
                  <span class="detail-label">Note</span>
                  <span class="detail-val note-val">{{ busChange.note }}</span>
                </div>
                <div class="detail-field mt-1" v-if="busChange.homePhone">
                  <span class="detail-label">Home Phone</span>
                  <span class="detail-val home-phone-val">{{ formatPhoneNumber(busChange.homePhone) }}</span>
                </div>

                <!-- Class & Teacher / DC room info -->
                <div
                  v-if="busChange.branch || busChange.class || busChange.building || busChange.section || busChange.teacherH || busChange.teacherH2 || getRoomDisplay(busChange)"
                  class="teacher-info-box mt-2"
                >
                  <div class="teacher-info-title">
                    <v-icon small class="mr-1" color="#2e7d32">mdi-school</v-icon>
                    {{
                      isBusChangeInSummerSeason(busChange)
                        ? "Class & Room"
                        : "Class & Teacher"
                    }}
                  </div>
                  <div class="teacher-fields-grid">
                    <div class="teacher-row" v-if="busChange.building">
                      <span class="teacher-label">Building</span>
                      <span class="teacher-val">{{ busChange.building }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.branch">
                      <span class="teacher-label">Branch</span>
                      <span class="teacher-val">{{ busChange.branch }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.class">
                      <span class="teacher-label">Class</span>
                      <span class="teacher-val">{{ busChange.class }}</span>
                    </div>
                    <div class="teacher-row" v-if="busChange.section">
                      <span class="teacher-label">Section</span>
                      <span class="teacher-val">{{ busChange.section }}</span>
                    </div>
                    <div
                      class="teacher-row"
                      v-if="
                        isBusChangeInSummerSeason(busChange) &&
                          getRoomDisplay(busChange)
                      "
                    >
                      <span class="teacher-label">DC Room</span>
                      <span class="teacher-val">{{
                        getRoomDisplay(busChange)
                      }}</span>
                    </div>
                    <div
                      class="teacher-row"
                      v-else-if="
                        !isBusChangeInSummerSeason(busChange) &&
                          (busChange.teacherH || busChange.teacherH2)
                      "
                    >
                      <span class="teacher-label">{{ busChange.teacherH2 ? "Teachers" : "Teacher" }}</span>
                      <span class="teacher-val teacher-val--rtl">
                        {{ busChange.teacherH }}
                        <template v-if="busChange.teacherH && busChange.teacherH2"> / </template>
                        {{ busChange.teacherH2 }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </v-flex>

            <!-- Right: routes + contact -->
            <v-flex xs12 sm6>
              <div class="detail-section">
                <div class="detail-section-title">
                  <v-icon small class="mr-1">mdi-phone</v-icon>
                  Routes &amp; Contact
                </div>
                <div class="detail-field">
                  <span class="detail-label">Pickup Route</span>
                  <span class="detail-val">{{
                    busChange.pickupRouteLabel || "—"
                  }}</span>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Dropoff Route</span>
                  <span class="detail-val">{{
                    busChange.dropoffRouteLabel || "—"
                  }}</span>
                </div>
                <div class="phone-row mt-2">
                  <div class="phone-item">
                    <v-icon small class="phone-icon">mdi-phone</v-icon>
                    <div>
                      <div class="detail-label">Bus Change Phone</div>
                      <div class="phone-number">
                        {{ formatPhoneNumber(busChange.busChangePhoneNumber) }}
                      </div>
                    </div>
                  </div>
                  <div class="phone-item">
                    <v-icon small class="phone-icon">mdi-message-text</v-icon>
                    <div>
                      <div class="detail-label">Notification</div>
                      <div class="phone-number">
                        {{ busChange.notificationType || "—" }}
                        <span
                          v-if="busChange.notificationPhoneNumber"
                          class="phone-sub"
                        >
                          {{
                            formatPhoneNumber(busChange.notificationPhoneNumber)
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="phone-item">
                    <v-icon small class="phone-icon"
                      >mdi-map-marker-radius</v-icon
                    >
                    <div>
                      <div class="detail-label">Geo Phone</div>
                      <div class="phone-number">
                        {{ formatPhoneNumber(busChange.geoPhoneNumber) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    loading: false,
    busRoutes: [],
    busRouteLookup: {},
    busChanges: [],
    summerDates: null,
    userDayCampBuildings: [],
    filters: {
      generalSearch: "",
      busNumber: "",
      phoneNumber: "",
      studentName: "",
      branch: null,
      status: null,
      includeCompleted: false,
      includeDeleted: false,
      activeOnCallDateOnly: true,
    },
  }),
  computed: {
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    availableBranches() {
      const set = {};
      this.busChanges.forEach((item) => {
        if (item.branch) set[item.branch] = true;
      });
      return Object.keys(set).sort();
    },
    availableStatuses() {
      const set = {};
      this.busChanges.forEach((item) => {
        if (item.status) set[item.status] = true;
      });
      return Object.keys(set).sort();
    },
    availableBusNumbers() {
      const set = {};
      this.busRoutes.forEach((r) => {
        const num = this.getRouteBusNumber(r);
        if (num) set[String(num)] = true;
      });
      return Object.keys(set).sort((a, b) => parseInt(a) - parseInt(b));
    },
    summerSeasonName() {
      const year =
        this.summerDates && this.summerDates.startDate
          ? moment(this.summerDates.startDate).year()
          : moment().year();
      return `Summer ${year}`;
    },
    filteredBusChanges() {
      const normalizedGeneral = this.normalizeText(this.filters.generalSearch);
      const normalizedBus = this.normalizeText(this.filters.busNumber);
      const normalizedStudent = this.normalizeText(this.filters.studentName);
      const normalizedPhone = this.normalizePhone(this.filters.phoneNumber);
      const todayDate = moment().format("YYYY-MM-DD");

      let filtered = this.busChanges.filter((item) => {
        const status = item.status ? item.status.toUpperCase() : "";
        if (!this.filters.includeCompleted && status === "COMPLETED")
          return false;
        if (!this.filters.includeDeleted && status === "DELETED") return false;

        if (this.filters.activeOnCallDateOnly) {
          const startDate = item.startDateForFilter || "";
          const endDate = item.endDateForFilter || "9999-12-31";
          if (!(startDate <= todayDate && endDate >= todayDate)) return false;
        }

        if (this.filters.branch && item.branch !== this.filters.branch)
          return false;
        if (this.filters.status && item.status !== this.filters.status)
          return false;

        if (
          normalizedBus &&
          !this.normalizeText(item.primaryBusNumber).includes(normalizedBus) &&
          !this.normalizeText(item.pickupBusNumber).includes(normalizedBus) &&
          !this.normalizeText(item.dropoffBusNumber).includes(normalizedBus) &&
          !this.normalizeText(item.pickupRouteLabel).includes(normalizedBus) &&
          !this.normalizeText(item.dropoffRouteLabel).includes(normalizedBus)
        ) {
          return false;
        }

        if (
          normalizedPhone &&
          !this.normalizePhone(item.busChangePhoneNumber).includes(
            normalizedPhone
          ) &&
          !this.normalizePhone(item.notificationPhoneNumber).includes(
            normalizedPhone
          ) &&
          !this.normalizePhone(item.geoPhoneNumber).includes(normalizedPhone) &&
          !this.normalizePhone(item.homePhone).includes(normalizedPhone)
        ) {
          return false;
        }

        if (
          normalizedStudent &&
          !this.normalizeText(item.studentName).includes(normalizedStudent) &&
          !this.normalizeText(item.studentNameHeb).includes(normalizedStudent)
        ) {
          return false;
        }


        if (normalizedGeneral) {
          const generalPhone = this.normalizePhone(this.filters.generalSearch);
          const textMatch = this.normalizeText(
            [
              item.studentName,
              item.studentNameHeb,
              item.familyID,
              item.studentID,
              item.branch,
              item.class,
              item.busChangeName,
              item.busChangeAddress,
              item.note,
              item.primaryBusNumber,
              item.pickupBusNumber,
              item.dropoffBusNumber,
              item.pickupRouteLabel,
              item.dropoffRouteLabel,
              item.teacherH,
              item.teacherH2,
              item.dcRoomNum,
              item.roomNum,
            ].join(" ")
          ).includes(normalizedGeneral);
          const phoneMatch =
            generalPhone.length >= 4 &&
            (this.normalizePhone(item.busChangePhoneNumber).includes(generalPhone) ||
              this.normalizePhone(item.notificationPhoneNumber).includes(generalPhone) ||
              this.normalizePhone(item.geoPhoneNumber).includes(generalPhone) ||
              this.normalizePhone(item.homePhone).includes(generalPhone));
          if (!textMatch && !phoneMatch) return false;
        }

        return true;
      });

      filtered.sort((a, b) => {
        const wa = this.getStatusWeight(a.status);
        const wb = this.getStatusWeight(b.status);
        if (wa !== wb) return wa - wb;
        if (a.startDateTimestamp !== b.startDateTimestamp) {
          return b.startDateTimestamp - a.startDateTimestamp;
        }
        return this.normalizeText(a.studentName).localeCompare(
          this.normalizeText(b.studentName)
        );
      });

      return filtered;
    },
  },
  watch: {
    "filters.status"(val) {
      if (!val) return;
      const upper = val.toUpperCase();
      if (upper === "COMPLETED") this.filters.includeCompleted = true;
      if (upper === "DELETED") this.filters.includeDeleted = true;
    },
  },
  created() {
    this.loadDispatcherData();
  },
  methods: {
    async loadDispatcherData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadUserDayCampBuildings(),
          this.loadSummerDates(),
        ]);
        await this.loadBusRoutes();
        await this.loadBusChanges();
        await this.loadTeacherInfo();
      } finally {
        this.loading = false;
      }
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },
    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;

      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );

        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
      } catch (e) {
        this.userDayCampBuildings = [];
      }
    },
    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },
    useDayCampBuildingFilter() {
      return this.hasUserDayCampBuildings() && this.isWithinSummerSeason();
    },
    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },
    isWithinSummerSeason() {
      if (!this.summerDates) return false;
      const today = moment().startOf("day");
      const start = moment(this.summerDates.startDate).startOf("day");
      const end = moment(this.summerDates.endDate).startOf("day");
      if (!start.isValid() || !end.isValid()) return false;
      return today.isSameOrAfter(start) && today.isSameOrBefore(end);
    },
    isBusChangeInSummerSeason(busChange) {
      if (!this.summerDates) return false;
      const seasonStart = moment(this.summerDates.startDate).startOf("day");
      const seasonEnd = moment(this.summerDates.endDate).startOf("day");
      if (!seasonStart.isValid() || !seasonEnd.isValid()) return false;
      const bcStart = busChange && busChange.startDate
        ? moment(busChange.startDate).startOf("day")
        : null;
      const bcEnd = busChange && busChange.endDate
        ? moment(busChange.endDate).startOf("day")
        : null;
      if (!bcStart || !bcStart.isValid()) return this.isWithinSummerSeason();
      const end = bcEnd && bcEnd.isValid() ? bcEnd : bcStart;
      return (
        bcStart.isSameOrBefore(seasonEnd) && end.isSameOrAfter(seasonStart)
      );
    },
    getRoomDisplay(item) {
      if (!item) return "";
      if (this.isBusChangeInSummerSeason(item)) {
        return item.dcRoomNum || "";
      }
      return item.roomNum || "";
    },
    getRouteBusNumber(route) {
      // Prefer actualBusNumber from GetBusRoutes when present; else busNumber.
      // Day camp routes often only populate actualBusNumber.
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },
    async loadBusRoutes() {
      try {
        // CurrentRouteSeason may still be school-year during summer, so also
        // load Summer {year} routes so DC pickup/dropoff IDs resolve.
        const urls = ["api/buschange/GetBusRoutes"];
        if (this.summerSeasonName) {
          urls.push(
            "api/buschange/GetBusRoutes?season=" +
              encodeURIComponent(this.summerSeasonName)
          );
        }
        const responses = await Promise.all(
          urls.map((url) =>
            this.$http.get(url).catch(() => [])
          )
        );
        const lookup = {};
        const merged = [];
        responses.forEach((routes) => {
          (Array.isArray(routes) ? routes : []).forEach((r) => {
            if (!r || r.id == null) return;
            const key = String(r.id);
            if (!lookup[key]) {
              lookup[key] = r;
              merged.push(r);
            }
          });
        });
        this.busRoutes = merged;
        this.busRouteLookup = lookup;
      } catch (error) {
        this.busRoutes = [];
        this.busRouteLookup = {};
        this.$notify({ type: "error", text: "Failed to load bus routes" });
      }
    },
    async loadBusChanges() {
      try {
        // Backend: building takes precedence; branch is only used when no building filter is sent.
        // Always include every query param key the OData Get declares, even when empty.
        const useBuilding = this.useDayCampBuildingFilter();
        const branchValue =
          !useBuilding && this.userBranch ? this.userBranch : "";
        const buildingValue = useBuilding
          ? this.userDayCampBuildings.join(",")
          : "";
        const url =
          "odata/busChange?branch=" +
          branchValue +
          "&building=" +
          buildingValue +
          "&includeCompleted=true&includeDeleted=true";
        const response = await this.$http.get(url);
        const rows = response && response.value ? response.value : [];
        this.busChanges = rows.map((raw) => this.mapBusChange(raw));
      } catch (error) {
        this.busChanges = [];
        this.$notify({ type: "error", text: "Failed to load bus changes" });
      }
    },
    async loadTeacherInfo() {
      const seen = {};
      const uniqueIDs = [];
      this.busChanges.forEach((bc) => {
        const id = bc.studentID ? String(bc.studentID).trim() : "";
        if (id && !seen[id]) {
          seen[id] = true;
          uniqueIDs.push(id);
        }
      });

      if (!uniqueIDs.length) return;

      try {
        const students = await this.$http.post(
          "api/BusChange/GetStudentInfoForStudentIDs",
          uniqueIDs
        );

        if (!Array.isArray(students)) return;

        const lookup = {};
        students.forEach((s) => {
          const id = s.studentID ? String(s.studentID).trim() : "";
          if (id) lookup[id] = s;
        });

        this.busChanges = this.busChanges.map((bc) => {
          const id = bc.studentID ? String(bc.studentID).trim() : "";
          const info = id ? lookup[id] : null;
          if (!info) return bc;
          return Object.assign({}, bc, {
            teacherH: String(
              info.teacherH || info.TeacherH || ""
            ).trim(),
            teacherH2: String(
              info.teacherH2 || info.TeacherH2 || ""
            ).trim(),
            roomNum: String(info.roomNum || info.RoomNum || "").trim(),
            dcRoomNum: String(
              info.dcRoomNum || info.DcRoomNum || ""
            ).trim(),
          });
        });
      } catch (error) {
        console.error("loadTeacherInfo failed:", error);
      }
    },
    mapBusChange(raw) {
      const pickupRoute = raw.pickupRouteID
        ? this.busRouteLookup[String(raw.pickupRouteID)]
        : null;
      const dropoffRoute = raw.dropoffRouteID
        ? this.busRouteLookup[String(raw.dropoffRouteID)]
        : null;

      return {
        id: raw.id,
        studentID: raw.studentID || "",
        familyID: raw.familyID || "",
        studentName: raw.studentName || "",
        studentNameHeb: raw.studentNameHeb || "",
        branch: raw.branch || "",
        class: raw.class || "",
        building: raw.building || "",
        section: raw.section || "",
        status: raw.status || "",
        startDate: raw.startDate || "",
        endDate: raw.endDate || "",
        startPeriod: raw.startPeriod || "",
        endPeriod: raw.endPeriod || "",
        startDateForFilter: this.formatDateForFilter(raw.startDate),
        endDateForFilter: this.formatDateForFilter(raw.endDate),
        startDateTimestamp: raw.startDate
          ? new Date(raw.startDate).getTime()
          : 0,
        busChangeName: raw.busChangeName || "",
        busChangeAddress: raw.busChangeAddress || "",
        busChangePhoneNumber: raw.busChangePhoneNumber || "",
        notificationPhoneNumber: raw.notificationPhoneNumber || "",
        notificationType: raw.notificationType || "",
        geoPhoneNumber: raw.geoPhoneNumber || "",
        homePhone: raw.homePhone || "",
        note: raw.note || "",
        teacherH: "",
        teacherH2: "",
        roomNum: "",
        dcRoomNum: "",
        pickupRouteID: raw.pickupRouteID || null,
        dropoffRouteID: raw.dropoffRouteID || null,
        pickupRouteLabel: this.formatRouteLabel(pickupRoute),
        dropoffRouteLabel: this.formatRouteLabel(dropoffRoute),
        pickupBusNumber: this.getRouteBusNumber(pickupRoute),
        dropoffBusNumber: this.getRouteBusNumber(dropoffRoute),
        primaryBusNumber: this.getPrimaryBusNumber(pickupRoute, dropoffRoute),
        hasDifferentBusNumbers: this.hasDifferentBusNumbers(
          pickupRoute,
          dropoffRoute
        ),
      };
    },
    formatRouteLabel(route) {
      if (!route) return "";
      const bus = this.getRouteBusNumber(route) || "No Bus";
      const driver = route.driver ? route.driver : "No Driver";
      return `${bus} — ${driver}`;
    },
    getPrimaryBusNumber(pickupRoute, dropoffRoute) {
      const pickup = this.getRouteBusNumber(pickupRoute);
      if (pickup) return pickup;
      const dropoff = this.getRouteBusNumber(dropoffRoute);
      if (dropoff) return dropoff;
      return "";
    },
    hasDifferentBusNumbers(pickupRoute, dropoffRoute) {
      const pickup = this.getRouteBusNumber(pickupRoute);
      const dropoff = this.getRouteBusNumber(dropoffRoute);
      return !!(pickup && dropoff && pickup !== dropoff);
    },
    normalizeText(value) {
      if (value === null || value === undefined) return "";
      return value
        .toString()
        .trim()
        .toLowerCase();
    },
    normalizePhone(value) {
      if (!value) return "";
      return value
        .toString()
        .replace(/\D/g, "")
        .trim();
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "—";
      const cleaned = this.normalizePhone(phoneNumber);
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      return phoneNumber;
    },
    formatDate(dateString) {
      if (!dateString) return "Open";
      return moment(dateString).format("ddd MM/DD/YYYY");
    },
    formatDateTime(dateString) {
      if (!dateString) return "—";
      return moment(dateString).format("MM/DD/YYYY h:mm A");
    },
    formatDateForFilter(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatPeriod(period) {
      if (!period) return "—";
      const n = period
        .toString()
        .trim()
        .toUpperCase();
      if (n === "AM") return "Morning Pickup";
      if (n === "PM") return "Afternoon Dropoff";
      return period;
    },
    formatPeriodShort(period) {
      if (!period) return "—";
      const n = period
        .toString()
        .trim()
        .toUpperCase();
      if (n === "AM") return "AM";
      if (n === "PM") return "PM";
      return period;
    },
    getStatusIcon(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "mdi-new-box";
      if (n === "FUTURE" || n === "PENDING") return "mdi-clock-outline";
      if (n === "ACTIVE") return "mdi-check-circle";
      if (n === "COMPLETED") return "mdi-check-all";
      if (n === "DELETED") return "mdi-delete";
      return "mdi-information";
    },
    getStatusColor(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "blue";
      if (n === "FUTURE" || n === "PENDING") return "orange";
      if (n === "ACTIVE") return "green";
      if (n === "COMPLETED") return "grey";
      if (n === "DELETED") return "red";
      return "indigo";
    },
    getStatusHex(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "NEW") return "#2196f3";
      if (n === "FUTURE" || n === "PENDING") return "#ff9800";
      if (n === "ACTIVE") return "#4caf50";
      if (n === "COMPLETED") return "#9e9e9e";
      if (n === "DELETED") return "#f44336";
      return "#5c6bc0";
    },
    getStatusWeight(status) {
      const n = status
        ? status
            .toString()
            .trim()
            .toUpperCase()
        : "";
      if (n === "ACTIVE") return 1;
      if (n === "PENDING" || n === "FUTURE") return 2;
      if (n === "NEW") return 3;
      if (n === "COMPLETED") return 4;
      if (n === "DELETED") return 5;
      return 6;
    },
    clearFilters() {
      this.filters.generalSearch = "";
      this.filters.busNumber = "";
      this.filters.phoneNumber = "";
      this.filters.studentName = "";
      this.filters.branch = null;
      this.filters.status = null;
      this.filters.includeCompleted = false;
      this.filters.includeDeleted = false;
      this.filters.activeOnCallDateOnly = false;
    },
  },
};
</script>

<style>
/* ── hero ── */
.bus-dispatching-page .dispatch-hero {
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 45%, #4caf50 100%);
  border-radius: 12px;
  padding: 20px 24px;
  color: #fff;
}

.bus-dispatching-page .dispatch-hero-icon {
  color: #fff !important;
  opacity: 0.9;
}

.bus-dispatching-page .dispatch-hero-title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.bus-dispatching-page .dispatch-hero-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 3px;
}

.bus-dispatching-page .dispatch-refresh-btn {
  border-color: rgba(255, 255, 255, 0.7) !important;
  color: #fff !important;
  font-weight: 600;
}

/* ── filter card ── */
.bus-dispatching-page .filter-card {
  border-radius: 10px;
  border: 1px solid #c8e6c9;
  overflow: hidden;
}

.bus-dispatching-page .filter-header {
  display: flex;
  align-items: center;
  background: #f1f8f1;
  border-bottom: 1px solid #c8e6c9;
  padding: 1px 16px;
}

.bus-dispatching-page .filter-header-icon {
  color: #2e7d32 !important;
  font-size: 18px !important;
}

.bus-dispatching-page .filter-header-title {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  letter-spacing: 0.3px;
}

.bus-dispatching-page .filter-clear-btn {
  color: #78909c !important;
  font-size: 12px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.bus-dispatching-page .filter-body {
  padding: 14px 16px 12px;
}

.bus-dispatching-page .filter-search-wrap .v-input__slot {
  box-shadow: 0 2px 8px rgba(46, 125, 50, 0.12) !important;
  border: 1.5px solid #c8e6c9 !important;
}

.bus-dispatching-page .filter-group-label {
  font-size: 11px;
  font-weight: 700;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}

.bus-dispatching-page .filter-fields-row {
  margin: 0 -6px;
}

.bus-dispatching-page .filter-field-wrap {
  padding: 0 6px;
}

.bus-dispatching-page .filter-toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f4f0;
}

.bus-dispatching-page .filter-toggle-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  border: 1.5px solid #cfd8dc;
  color: #78909c;
  background: #fafafa;
  transition: all 0.18s ease;
}

.bus-dispatching-page .filter-toggle-pill:hover {
  border-color: #90a4ae;
  color: #546e7a;
}

.bus-dispatching-page .filter-toggle-pill--on {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #1b5e20;
}

.bus-dispatching-page .filter-toggle-pill--on .v-icon {
  color: #1b5e20 !important;
}

.bus-dispatching-page .filter-toggle-pill--completed {
  background: #e0f2f1;
  border-color: #00897b;
  color: #004d40;
}

.bus-dispatching-page .filter-toggle-pill--completed .v-icon {
  color: #004d40 !important;
}

.bus-dispatching-page .filter-toggle-pill--deleted {
  background: #fce4ec;
  border-color: #e53935;
  color: #b71c1c;
}

.bus-dispatching-page .filter-toggle-pill--deleted .v-icon {
  color: #b71c1c !important;
}

/* ── results bar ── */
.bus-dispatching-page .results-bar {
  padding: 8px 2px;
}

.bus-dispatching-page .results-count {
  font-size: 15px;
  color: #37474f;
}

.bus-dispatching-page .dispatch-view-badge {
  font-size: 13px;
  color: #2e7d32;
  font-weight: 700;
  display: flex;
  align-items: center;
}

/* ── panels container ── */
.bus-dispatching-page .dispatch-panels {
  box-shadow: none !important;
}

.bus-dispatching-page .dispatch-panels .v-expansion-panel__container {
  border: 1px solid #e0e6ef !important;
  border-radius: 10px !important;
  margin-bottom: 8px !important;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07) !important;
}

.bus-dispatching-page
  .dispatch-panels
  .v-expansion-panel__container
  + .v-expansion-panel__container {
  border-top: 1px solid #e0e6ef !important;
}

/* ── panel header ── */
.bus-dispatching-page .v-expansion-panel__header {
  padding: 0 !important;
  min-height: 72px;
}

.bus-dispatching-page .panel-header-inner {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 72px;
  padding-right: 12px;
}

.bus-dispatching-page .panel-status-bar {
  width: 5px;
  min-height: 72px;
  border-radius: 0;
  flex-shrink: 0;
  margin-right: 16px;
}

.bus-dispatching-page .panel-col {
  flex-shrink: 0;
  padding-right: 20px;
}

.bus-dispatching-page .panel-name-col {
  flex: 1 1 0;
  min-width: 0;
}

.bus-dispatching-page .panel-bus-col {
  width: 160px;
}

.bus-dispatching-page .panel-bus-dual-row {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-top: 2px;
}

.bus-dispatching-page .panel-bus-badge--sm {
  font-size: 16px;
  padding: 3px 7px;
}

.bus-dispatching-page .panel-dates-col {
  width: 170px;
}

.bus-dispatching-page .panel-period-col {
  width: 120px;
}

.bus-dispatching-page .panel-contact-col {
  width: 180px;
  overflow: hidden;
}

.bus-dispatching-page .panel-classtch-col {
  width: 180px;
  overflow: hidden;
}

.bus-dispatching-page .panel-contact-name {
  font-size: 13px;
  font-weight: 600;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.bus-dispatching-page .panel-contact-phone {
  font-size: 13px;
  font-weight: 700;
  color: #2e7d32;
  display: flex;
  align-items: center;
}

.bus-dispatching-page .panel-contact-teacher {
  font-size: 12px;
  color: #546e7a;
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 2px;
}

.bus-dispatching-page .panel-teacher-text {
  direction: rtl;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.bus-dispatching-page .panel-status-col {
  width: 120px;
  display: flex;
  align-items: center;
  padding-right: 0;
}

.bus-dispatching-page .panel-student-name {
  font-size: 13px;
  font-weight: 500;
  color: #546e7a;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bus-dispatching-page .panel-student-heb {
  font-size: 20px;
  font-weight: 700;
  color: #1a2f45;
  line-height: 1.2;
  direction: rtl;
}

.bus-dispatching-page .panel-ids {
  font-size: 12px;
  color: #78909c;
  margin-top: 2px;
}

.bus-dispatching-page .panel-field-label {
  font-size: 10px;
  font-weight: 700;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.bus-dispatching-page .panel-field-value {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}

.bus-dispatching-page .panel-dates-end {
  font-size: 13px;
  font-weight: 400;
  color: #546e7a;
}

.bus-dispatching-page .panel-bus-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  border: 2px solid #4caf50;
  font-size: 20px;
  font-weight: 800;
  color: #1a2f45;
  background: #f8fffe;
  margin-top: 2px;
}

.bus-dispatching-page .panel-bus-dir {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #78909c;
  margin-right: 5px;
  line-height: 1;
}

.bus-dispatching-page .panel-status-chip {
  font-size: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
}

/* ── panel body ── */
.bus-dispatching-page .panel-body {
  padding: 0 16px 20px 21px;
  border-top: 1px solid #f0f4f8;
  background: #fafcff;
}

.bus-dispatching-page .detail-section {
  padding: 14px 12px 10px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8edf3;
  margin-top: 12px;
  height: calc(100% - 12px);
}

.bus-dispatching-page .detail-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #455a64;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.bus-dispatching-page .detail-field {
  display: flex;
  align-items: flex-start;
  margin-bottom: 7px;
}

.bus-dispatching-page .detail-label {
  font-size: 13px;
  font-weight: 700;
  color: #546e7a;
  width: 130px;
  flex-shrink: 0;
}

.bus-dispatching-page .detail-val {
  font-size: 15px;
  font-weight: 500;
  color: #1a237e;
  flex: 1;
}

.bus-dispatching-page .note-val {
  font-style: italic;
  color: #546e7a;
}

.bus-dispatching-page .home-phone-val {
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
}

/* ── phone rows ── */
.bus-dispatching-page .phone-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.bus-dispatching-page .phone-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 130px;
}

.bus-dispatching-page .phone-icon {
  margin-top: 14px;
  color: #78909c !important;
}

.bus-dispatching-page .phone-number {
  font-size: 15px;
  font-weight: 700;
  color: #2e7d32;
}

.bus-dispatching-page .phone-sub {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: #546e7a;
}

/* ── teacher info box ── */
.bus-dispatching-page .teacher-info-box {
  background: #f1f8f1;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 10px 12px;
}

.bus-dispatching-page .teacher-info-title {
  font-size: 12px;
  font-weight: 700;
  color: #2e7d32;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.bus-dispatching-page .teacher-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
}

.bus-dispatching-page .teacher-row {
  display: flex;
  align-items: baseline;
}

.bus-dispatching-page .teacher-label {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  width: 64px;
  flex-shrink: 0;
}

.bus-dispatching-page .teacher-val {
  font-size: 14px;
  font-weight: 700;
  color: #1b5e20;
}

.bus-dispatching-page .teacher-val--rtl {
  direction: rtl;
}
</style>
